import PrimaryButton from "components/buttons/PrimaryButton";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { t } from "i18n/index";
import BusinessSignup from "../BusinessSignup";
import CreatorSignup from "../CreatorSignup";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Faq from "../components/Faq";
import FeatureSection from "../components/FeatureSection";
import Footer from "../components/Footer";
import HowStep from "../components/HowStep";
import OutroForm from "../components/OutroForm";
import UspItem from "../components/UspItem";
import Navigation from "../components/navigation/Navigation";
import { getBusinessPageTranslations } from "../marketing/i18n/pages/businessPage";
import "../css/BusinessPage.scss";
import "../css/FeaturePage.scss";

const BusinessPage = ({
  title = "",
  text = "",
  subtitle = "",
  usps = [],
  featureList,
  howitworks,
  faq = false,
  color = "default",
  type = "blogger",
  form = "creator",
  signupType = undefined,
  pageTitle = "",
  lang = "en",
}) => {
  const translations = getBusinessPageTranslations(lang || "en");
  const featureContainer = useRef();
  const [activeFeature, setActiveFeature] = useState(featureList[0]);
  // Default to desktop layout server-side; flip on mount to match the actual device.
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileOnly);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className={`feature-page business-page ${color}`}>
      <Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />
      <div className={`feature-header ${type}`}>
        <div className="left-content">
          <div className="content-container">
            <p className="sub-header">{t(translations, `businessPage.${type}.subtitle`, subtitle)}</p>
            <h1 className="small">{t(translations, `businessPage.${type}.title`, title)}</h1>
            <p>{t(translations, `businessPage.${type}.text`, text)}</p>
            <ul>
              {usps.map((usp, index) => (
                <UspItem text={t(translations, `businessPage.${type}.usps.${index}`, usp)} />
              ))}
            </ul>
            <PrimaryButton
              label={t(translations, "businessPage.chrome.learnMore", "Learn more")}
              size="large"
              color="black"
              type="outlined"
              //@ts-ignore
              onClick={() => featureContainer?.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            />
          </div>
        </div>
        <div className="right-content">
          <div className="cta-form">
            {form === "business" ? <BusinessSignup type={type} signupType={signupType} /> : <CreatorSignup type={type} />}
          </div>
        </div>
      </div>
      <div className="container__full-width">
        <div className="container__inner">
          <section className="how-it-works" ref={featureContainer}>
            <div className="text-header-section">
              <p className="sub-header">{t(translations, "businessPage.chrome.howItWorksLabel", "Getting started")}</p>
              <h2>{t(translations, "businessPage.chrome.howItWorksHeading", "How it works")}</h2>
            </div>
            <div className="how-steps">
              {howitworks?.map((step, index) => (
                <HowStep title={step.title} text={step.text} icon={step.image} count={index + 1} />
              ))}
            </div>
          </section>
          <div className="feature-container">
            {!isMobile ? (
              <>
                <div className="feature-list">
                  {featureList.map((feature) => (
                    <FeatureSection feature={feature} setActiveFeature={setActiveFeature} />
                  ))}
                </div>
                <div className="feature-image-container">
                  <div className={`image-card ${color}`}>
                    {featureList.map((feature) => (
                      <AnimatePresence>
                        {feature?.image === activeFeature.image ? (
                          <motion.div
                            className="feature-image"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <picture>
                              <source srcSet={feature?.image} type="image/webp" />
                              <img loading="lazy" src={feature?.image} alt="Transport visual" />
                            </picture>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="feature-list">
                {featureList.map((feature) => (
                  <>
                    <div className="feature-image">
                      <picture>
                        <source srcSet={feature?.image} type="image/webp" />
                        <img loading="lazy" src={feature?.image} alt="Transport visual" />
                      </picture>
                    </div>

                    <div className="feature-item">
                      <div className="content-container">
                        <p className="sub-header">{feature.subtitle}</p>
                        <h2>{feature.title}</h2>
                        <p className="text">{feature.text}</p>
                      </div>
                    </div>
                  </>
                ))}
              </div>
            )}
          </div>
          {faq ? (
            <section className="faq-section">
              <h2>{t(translations, "businessPage.faq.heading", "FAQ")}</h2>
              <Faq
                title={t(translations, "businessPage.faq.0.question", "Is Stippl for travel agents or content creators?")}
                description={t(
                  translations,
                  "businessPage.faq.0.answer",
                  "Both. Stippl is built for anyone who plans trips for an audience: travel agents, tour operators, content creators, bloggers and influencers. The same tools (itineraries, maps, branded PDFs and reels) work whether you're sending an itinerary to a paying client or sharing it with your followers."
                )}
              />
              <Faq
                title={t(translations, "businessPage.faq.1.question", "Do I need to re-create all my existing itineraries?")}
                description={t(
                  translations,
                  "businessPage.faq.1.answer",
                  "No. You can paste in your existing notes, links and recommendations and turn them into a Stippl itinerary in minutes. Most creators import a few of their best trips first, then add more over time."
                )}
              />
              <Faq
                title={t(translations, "businessPage.faq.2.question", "What is a Stippl Travel Reel?")}
                description={t(
                  translations,
                  "businessPage.faq.2.answer",
                  "A fully branded travel video generated automatically from your itinerary: your logo, your colours, your style. Share it on Instagram or TikTok to drive your audience back to your itineraries and affiliate links. No editing app or design skills needed."
                )}
              />
              <Faq
                title={t(translations, "businessPage.faq.3.question", "How much does it cost to get started?")}
                description={t(
                  translations,
                  "businessPage.faq.3.answer",
                  "You can start a free trial with no credit card required. After the trial you can pick a plan that matches the size of your audience or client base. Full pricing details are on our pricing page."
                )}
              />
              <Faq
                title={t(translations, "businessPage.faq.4.question", "Why should I embed a Stippl map instead of Google Maps?")}
                description={t(
                  translations,
                  "businessPage.faq.4.answer",
                  "Stippl maps stay on-brand, link directly to your itineraries and recommendations, and let your readers tap through to your affiliate links. Google Maps sends your traffic to Google, Stippl keeps it on you."
                )}
              />
              <Faq
                title={t(translations, "businessPage.faq.5.question", "What are the requirements to get started?")}
                description={t(
                  translations,
                  "businessPage.faq.5.answer",
                  "Just a website, blog or social channel where you share travel content and an audience you'd like to plan trips for. Sign up for a free trial and you can be live the same day."
                )}
              />
            </section>
          ) : null}
        </div>
      </div>

      <OutroForm form={form} type={type} signupType={signupType} />
      <Footer />
    </div>
  );
};

export default BusinessPage;
